import { SnowplowExtraContext } from '@utilitywarehouse/tracking-adapter-snowplow-web';
import {
  Context,
  EventTuple,
  ExtraContext,
} from '@utilitywarehouse/tracking-core';
import { useTracking, useTrackOnce } from '@utilitywarehouse/tracking-react';

type EntityPayload<T> = ExtraContext<T> | SnowplowExtraContext<T>;

interface Options extends Record<string, unknown> {
  contexts?: Array<ExtraContext | SnowplowExtraContext>;
}

export interface PsuPageQuoteButtonClickedEventContext {
  // The section of the page where the quote button was clicked
  page_section?: string;
}

// The user clicks the quote button on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageQuoteButtonClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageQuoteButtonClickedEvent(
  context: PsuPageQuoteButtonClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_quote_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_quote_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageQuoteButtonClicked() {
  const { track } = useTracking();

  return (context: PsuPageQuoteButtonClickedEventContext, options?: Options) =>
    track(psuPageQuoteButtonClickedEvent(context, options));
}

export function useTrackPsuPageQuoteButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PsuPageQuoteButtonClickedEventContext, options?: Options) =>
    trackOnce(psuPageQuoteButtonClickedEvent(context, options));
}

export interface PsuPageContactMeButtonClickedEventContext {
  // The section of the page where the contact me button was clicked
  page_section?: string;
}

// The user clicks the contact me button on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageContactMeButtonClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageContactMeButtonClickedEvent(
  context: PsuPageContactMeButtonClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_contact_me_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_contact_me_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageContactMeButtonClicked() {
  const { track } = useTracking();

  return (
    context: PsuPageContactMeButtonClickedEventContext,
    options?: Options,
  ) => track(psuPageContactMeButtonClickedEvent(context, options));
}

export function useTrackPsuPageContactMeButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageContactMeButtonClickedEventContext,
    options?: Options,
  ) => trackOnce(psuPageContactMeButtonClickedEvent(context, options));
}

export interface PsuPageContactMeButtonDisplayedEventContext {
  // The section of the page where the contact me button was displayed
  page_section?: string;
}

// The contact me button is displayed on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageContactMeButtonDisplayed({prop: 'value'}, {option1: 'value'}));
export function psuPageContactMeButtonDisplayedEvent(
  context: PsuPageContactMeButtonDisplayedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_contact_me_button_displayed',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_contact_me_button_displayed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageContactMeButtonDisplayed() {
  const { track } = useTracking();

  return (
    context: PsuPageContactMeButtonDisplayedEventContext,
    options?: Options,
  ) => track(psuPageContactMeButtonDisplayedEvent(context, options));
}

export function useTrackPsuPageContactMeButtonDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageContactMeButtonDisplayedEventContext,
    options?: Options,
  ) => trackOnce(psuPageContactMeButtonDisplayedEvent(context, options));
}

// The user clicks the view fewer reasons button on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageFewerReasonsButtonClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageFewerReasonsButtonClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    'psu_page_fewer_reasons_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_fewer_reasons_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFewerReasonsButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(psuPageFewerReasonsButtonClickedEvent(context, options));
}

export function useTrackPsuPageFewerReasonsButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(psuPageFewerReasonsButtonClickedEvent(context, options));
}

export interface PsuPageLegalsClickedEventContext {
  // Whether the legal copy was opened or closed
  legals_opened?: boolean;
}

// The user clicks on the "The legal bit" button on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageLegalsClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageLegalsClickedEvent(
  context: PsuPageLegalsClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_legals_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_legals_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageLegalsClicked() {
  const { track } = useTracking();

  return (context: PsuPageLegalsClickedEventContext, options?: Options) =>
    track(psuPageLegalsClickedEvent(context, options));
}

export function useTrackPsuPageLegalsClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PsuPageLegalsClickedEventContext, options?: Options) =>
    trackOnce(psuPageLegalsClickedEvent(context, options));
}

export interface PsuPageFaqClickedEventContext {
  // The FAQ question that was clicked
  faq_question?: string;
  // Whether the FAQ was opened or closed
  faq_opened?: boolean;
}

// The user clicks an FAQ item on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageFaqClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageFaqClickedEvent(
  context: PsuPageFaqClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_faq_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_faq_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFaqClicked() {
  const { track } = useTracking();

  return (context: PsuPageFaqClickedEventContext, options?: Options) =>
    track(psuPageFaqClickedEvent(context, options));
}

export function useTrackPsuPageFaqClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PsuPageFaqClickedEventContext, options?: Options) =>
    trackOnce(psuPageFaqClickedEvent(context, options));
}

export interface PsuPageMoreReasonsButtonClickedEventContext {
  // Whether the extra reasons were shown or hidden
  more_reasons_opened?: boolean;
}

// The user clicks the view more/less reasons button on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageMoreReasonsButtonClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageMoreReasonsButtonClickedEvent(
  context: PsuPageMoreReasonsButtonClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_more_reasons_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_more_reasons_button_clicked/jsonschema/1-0-2',
      },
    },
  ];
}

export function useTrackPsuPageMoreReasonsButtonClicked() {
  const { track } = useTracking();

  return (
    context: PsuPageMoreReasonsButtonClickedEventContext,
    options?: Options,
  ) => track(psuPageMoreReasonsButtonClickedEvent(context, options));
}

export function useTrackPsuPageMoreReasonsButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageMoreReasonsButtonClickedEventContext,
    options?: Options,
  ) => trackOnce(psuPageMoreReasonsButtonClickedEvent(context, options));
}

export interface PsuPageFormContactInfoClickedEventContext {
  // The type of contact info item that was clicked, e.g. 'email', 'phone', etc.
  contact_info_type?: string;
}

// The user clicks on a contact info item on the contact form on the Partner's personalised sign-up (PSU) page.
// example usage: tracker.track(trackPsuPageFormContactInfoClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageFormContactInfoClickedEvent(
  context: PsuPageFormContactInfoClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_contact_info_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_contact_info_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormContactInfoClicked() {
  const { track } = useTracking();

  return (
    context: PsuPageFormContactInfoClickedEventContext,
    options?: Options,
  ) => track(psuPageFormContactInfoClickedEvent(context, options));
}

export function useTrackPsuPageFormContactInfoClickedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageFormContactInfoClickedEventContext,
    options?: Options,
  ) => trackOnce(psuPageFormContactInfoClickedEvent(context, options));
}

export interface PsuPageFormFieldEditedEventContext {
  // The name of the form field that was edited
  form_field_name?: string;
}

// The user edits a field in the contact form on the Partner's personalised sign-up (PSU) page.
// example usage: tracker.track(trackPsuPageFormFieldEdited({prop: 'value'}, {option1: 'value'}));
export function psuPageFormFieldEditedEvent(
  context: PsuPageFormFieldEditedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_field_edited',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_field_edited/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormFieldEdited() {
  const { track } = useTracking();

  return (context: PsuPageFormFieldEditedEventContext, options?: Options) =>
    track(psuPageFormFieldEditedEvent(context, options));
}

export function useTrackPsuPageFormFieldEditedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PsuPageFormFieldEditedEventContext, options?: Options) =>
    trackOnce(psuPageFormFieldEditedEvent(context, options));
}

// The user clicks the contact form close button on the Partner's personalised sign-up (PSU) page
// example usage: tracker.track(trackPsuPageFormCloseButtonClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageFormCloseButtonClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_close_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_close_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormCloseButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(psuPageFormCloseButtonClickedEvent(context, options));
}

export function useTrackPsuPageFormCloseButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(psuPageFormCloseButtonClickedEvent(context, options));
}

export interface PsuPageFormSubmitDetailsFailedEventContext {
  // The type of error that occurred during form submission
  error_type?: string;
}

// The user fails to submit the contact form on the Partner's personalised sign-up (PSU) page.
// example usage: tracker.track(trackPsuPageFormSubmitDetailsFailed({prop: 'value'}, {option1: 'value'}));
export function psuPageFormSubmitDetailsFailedEvent(
  context: PsuPageFormSubmitDetailsFailedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_submit_details_failed',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_submit_details_failed/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormSubmitDetailsFailed() {
  const { track } = useTracking();

  return (
    context: PsuPageFormSubmitDetailsFailedEventContext,
    options?: Options,
  ) => track(psuPageFormSubmitDetailsFailedEvent(context, options));
}

export function useTrackPsuPageFormSubmitDetailsFailedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageFormSubmitDetailsFailedEventContext,
    options?: Options,
  ) => trackOnce(psuPageFormSubmitDetailsFailedEvent(context, options));
}

export interface PsuPageFormSubmitDetailsSucceededEventContext {
  // The data types submitted with the form e.g. 'email', 'phone', etc.
  data_types_added?: string[];
}

// The user successfully submits the contact form on the Partner's personalised sign-up (PSU) page.
// example usage: tracker.track(trackPsuPageFormSubmitDetailsSucceeded({prop: 'value'}, {option1: 'value'}));
export function psuPageFormSubmitDetailsSucceededEvent(
  context: PsuPageFormSubmitDetailsSucceededEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_submit_details_succeeded',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_submit_details_succeeded/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormSubmitDetailsSucceeded() {
  const { track } = useTracking();

  return (
    context: PsuPageFormSubmitDetailsSucceededEventContext,
    options?: Options,
  ) => track(psuPageFormSubmitDetailsSucceededEvent(context, options));
}

export function useTrackPsuPageFormSubmitDetailsSucceededOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageFormSubmitDetailsSucceededEventContext,
    options?: Options,
  ) => trackOnce(psuPageFormSubmitDetailsSucceededEvent(context, options));
}

// The user clicks the contact form submit button on the Partner's personalised sign-up (PSU) page.
// example usage: tracker.track(trackPsuPageFormSubmitButtonClicked({prop: 'value'}, {option1: 'value'}));
export function psuPageFormSubmitButtonClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_submit_button_clicked',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_submit_button_clicked/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormSubmitButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(psuPageFormSubmitButtonClickedEvent(context, options));
}

export function useTrackPsuPageFormSubmitButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(psuPageFormSubmitButtonClickedEvent(context, options));
}

export interface PsuPageFormContactInfoCopiedEventContext {
  // The type of contact info item that was copied, e.g. 'email', 'phone', etc.
  contact_info_type?: string;
}

// The user clicks on the copy to clipboard button for a contact info item on the contact form on the Partner's personalised sign-up (PSU) page.
// example usage: tracker.track(trackPsuPageFormContactInfoCopied({prop: 'value'}, {option1: 'value'}));
export function psuPageFormContactInfoCopiedEvent(
  context: PsuPageFormContactInfoCopiedEventContext,
  options?: Options,
): EventTuple {
  return [
    'psu_page_form_contact_info_copied',
    context,
    {
      ...options,
      snowplow: {
        schema:
          'iglu:uk.co.uw.partner-personalised-page/psu_page_form_contact_info_copied/jsonschema/1-0-0',
      },
    },
  ];
}

export function useTrackPsuPageFormContactInfoCopied() {
  const { track } = useTracking();

  return (
    context: PsuPageFormContactInfoCopiedEventContext,
    options?: Options,
  ) => track(psuPageFormContactInfoCopiedEvent(context, options));
}

export function useTrackPsuPageFormContactInfoCopiedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: PsuPageFormContactInfoCopiedEventContext,
    options?: Options,
  ) => trackOnce(psuPageFormContactInfoCopiedEvent(context, options));
}

export interface PsuPagePartnerReferralEntity {
  // The short lead code used for referring the prospect. The value defaults to `direct` when no lead code is provided.
  lead_code: string;
  // The partner position that referred the prospect.
  referring_position_id: string;
  // The unique identifier for the instance of the referral - combines the lead_code, referring_position_id and a timestamp of when the event triggered.
  unique_instance_id: string;
}

// Referral entity for referrals that originate from a partner.
export function psuPagePartnerReferralEntity(
  context: PsuPagePartnerReferralEntity,
  options?: Options,
): EntityPayload<PsuPagePartnerReferralEntity> {
  return {
    ...options,
    name: 'psu_page_partner_referral',
    data: context,
    snowplow: {
      schema:
        'iglu:uk.co.uw.partner-personalised-page/psu_page_partner_referral/jsonschema/1-0-1',
    },
  };
}
